import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import { DAY_VALUES, dayShort } from 'components/play/constants';

/**
 * OTRIX Play — tira de días de la semana (Lun..Dom) para navegar la rutina día a día.
 * El día seleccionado se marca en dorado; el día de hoy lleva un punto; un día completo
 * (>= minItems) muestra el check, y si tiene ejercicios pero no llega al mínimo muestra
 * el conteo. Sustituye al stack de acordeones.
 *
 * En mobile es un carrusel con scroll-snap que arranca centrado en el día ELEGIDO (por defecto
 * hoy): sin esto la tira empezaba siempre en lunes y había que scrollear para ver lo que toca.
 */
function RoutineDayTabs({ selectedDay, todayDay, countsByDay, minItems, onSelect }) {
  const { t } = useTranslation(['play']);
  const selectedRef = useRef(null);

  useEffect(() => {
    // `inline: 'center'` es lo que posiciona el carrusel; `block: 'nearest'` evita arrastrar
    // la página al hacerlo.
    const el = selectedRef.current;
    if (el?.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  }, [selectedDay]);

  return (
    <Stack
      direction="row"
      spacing={1}
      role="tablist"
      aria-label={t('routine.daysAria', { ns: 'play' })}
      sx={{
        width: '100%',
        overflowX: 'auto',
        pb: 0.5,
        scrollSnapType: { xs: 'x mandatory', md: 'none' },
        // La tira scrollea sola en mobile; el contenedor de la página nunca se desborda.
        '&::-webkit-scrollbar': { height: 0 },
        scrollbarWidth: 'none'
      }}>
      {DAY_VALUES.map((day) => {
        const count = countsByDay[day] || 0;
        const active = day === selectedDay;
        const complete = count >= minItems;
        const isToday = day === todayDay;

        return (
          <Box
            key={day}
            ref={active ? selectedRef : null}
            role="tab"
            tabIndex={0}
            aria-selected={active}
            onClick={() => onSelect(day)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(day);
              }
            }}
            sx={{
              flex: '1 0 auto',
              minWidth: 64,
              scrollSnapAlign: { xs: 'center', md: 'none' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              cursor: 'pointer',
              border: '1px solid',
              // Selección NEUTRA (gris) — el dorado queda para el dot de "hoy".
              borderColor: active ? 'hairline.strong' : 'hairline.main',
              bgcolor: active ? 'selected.bg' : 'surface.raised',
              transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), border-color 0.2s, background-color 0.2s',
              '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' },
              '&:active': { transform: 'scale(0.98)' }
            }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{ textTransform: 'capitalize', color: active ? 'selected.fg' : 'text.primary' }}>
                {dayShort(t, day)}
              </Typography>
              {isToday && <Box sx={{ width: 4, height: 4, borderRadius: '50%',
 flex: 'none', bgcolor: 'gold.main' }} />}
            </Stack>

            {/* Ranura de estado de altura fija: el check no debe empujar el layout. */}
            <Box sx={{ height: 16, display: 'flex', alignItems: 'center' }}>
              {complete ? (
                <Check sx={{ fontSize: 14, color: active ? 'selected.fg' : 'text.secondary' }} />
              ) : (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  {count > 0 ? count : '·'}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

RoutineDayTabs.propTypes = {
  selectedDay: PropTypes.number.isRequired,
  todayDay: PropTypes.number.isRequired,
  /** { [isoDay]: nº de ejercicios } */
  countsByDay: PropTypes.objectOf(PropTypes.number).isRequired,
  minItems: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired
};

export default RoutineDayTabs;
